import { useState, useMemo } from "react";
import { Slider } from "@/components/ui/slider";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Calculator, TrendingUp } from "lucide-react";

export default function RevenueCalculator() {
  const [price, setPrice] = useState(49);
  const [customers, setCustomers] = useState(50);
  const [growth, setGrowth] = useState(12);
  const [churn, setChurn] = useState(5);
  const [cac, setCac] = useState(120);

  const { mrr, arr, ltv, ratio, forecast, breakeven } = useMemo(() => {
    const mrr = price * customers;
    const arr = mrr * 12;
    const avgLifetime = churn > 0 ? 1 / (churn / 100) : 36;
    const ltv = price * avgLifetime;
    const ratio = cac > 0 ? ltv / cac : 0;
    let cust = customers;
    const forecast = [];
    let breakeven = null;
    for (let m = 0; m <= 12; m++) {
      const rev = Math.round(price * cust);
      forecast.push({ month: `M${m}`, mrr: rev });
      if (breakeven === null && rev * avgLifetime >= cac * cust * 0.3) breakeven = m;
      cust = cust * (1 + growth / 100) * (1 - churn / 100);
    }
    return { mrr, arr, ltv: Math.round(ltv), ratio: ratio.toFixed(1), forecast, breakeven };
  }, [price, customers, growth, churn, cac]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="text-center mb-10">
        <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mx-auto mb-4"><Calculator className="w-6 h-6 text-emerald-400" /></div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-50">SaaS Revenue Calculator</h1>
        <p className="text-slate-400 mt-2">Model your MRR, ARR, LTV and unit economics.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="glass rounded-2xl p-6 space-y-6">
          <SliderRow label="Monthly price" value={price} set={setPrice} min={5} max={500} step={5} prefix="$" testId="revenue-calculator-price-slider" />
          <SliderRow label="Current customers" value={customers} set={setCustomers} min={1} max={2000} step={1} testId="revenue-calculator-customers-slider" />
          <SliderRow label="Monthly growth rate" value={growth} set={setGrowth} min={0} max={40} step={1} suffix="%" testId="revenue-calculator-growth-slider" />
          <SliderRow label="Monthly churn" value={churn} set={setChurn} min={0} max={20} step={1} suffix="%" testId="revenue-calculator-churn-slider" />
          <SliderRow label="Customer acquisition cost" value={cac} set={setCac} min={0} max={1000} step={10} prefix="$" testId="revenue-calculator-cac-slider" />
        </div>

        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <Stat label="MRR" value={`$${mrr.toLocaleString()}`} accent="#10B981" />
            <Stat label="ARR" value={`$${arr.toLocaleString()}`} accent="#06B6D4" />
            <Stat label="LTV" value={`$${ltv.toLocaleString()}`} accent="#6366F1" />
            <Stat label="LTV : CAC" value={`${ratio}x`} accent={parseFloat(ratio) >= 3 ? "#10B981" : "#F59E0B"} />
          </div>
          <div className="glass rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-4"><TrendingUp className="w-4 h-4 text-emerald-400" /><h4 className="font-semibold text-slate-100 text-sm">12-Month MRR Forecast</h4></div>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={forecast}>
                <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10B981" stopOpacity={0.4} /><stop offset="100%" stopColor="#10B981" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="month" stroke="#64748B" fontSize={11} />
                <YAxis stroke="#64748B" fontSize={11} tickFormatter={(v) => `$${v >= 1000 ? (v / 1000).toFixed(0) + "k" : v}`} />
                <Tooltip contentStyle={{ background: "#111827", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff" }} formatter={(v) => [`$${v.toLocaleString()}`, "MRR"]} />
                <Area type="monotone" dataKey="mrr" stroke="#10B981" strokeWidth={2} fill="url(#g)" />
              </AreaChart>
            </ResponsiveContainer>
            <p className="text-xs text-slate-500 mt-2 font-mono">Projected Month 12 MRR: <span className="text-emerald-400">${forecast[12]?.mrr.toLocaleString()}</span></p>
          </div>
        </div>
      </div>
    </div>
  );
}

function SliderRow({ label, value, set, min, max, step, prefix = "", suffix = "", testId }) {
  return (
    <div>
      <div className="flex justify-between mb-2"><span className="text-sm text-slate-400">{label}</span><span className="font-mono text-cyan-400 font-bold">{prefix}{value.toLocaleString()}{suffix}</span></div>
      <Slider value={[value]} onValueChange={(v) => set(v[0])} min={min} max={max} step={step} data-testid={testId} />
    </div>
  );
}
function Stat({ label, value, accent }) {
  return <div className="glass rounded-xl p-5"><p className="text-xs uppercase tracking-wide text-slate-500 mb-1">{label}</p><p className="font-mono text-2xl font-bold" style={{ color: accent }}>{value}</p></div>;
}
